import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUtensils, FaLeaf, FaPlus, FaCheckCircle, FaExclamationTriangle, FaTrash, FaSave, FaTimes, FaSearch, FaChevronDown } from 'react-icons/fa';
import { UserContext } from '../../Context/UserContext';
import axios from 'axios';
import './CreateCustomerPage.css';

// Reusable Searchable Dropdown Component
const SearchableDropdown = ({ options, value, onChange, placeholder = 'Select...', style = {}, menuStyle = {}, id = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setTimeout(() => {
        if (searchInputRef.current) searchInputRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  const normalizedOptions = useMemo(() => {
    return (options || []).map(opt => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return {
        value: opt.value ?? opt._id ?? opt.id ?? opt.name,
        label: opt.label ?? opt.name ?? opt.itemName ?? opt.item_name ?? String(opt.value)
      };
    });
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return normalizedOptions;
    const term = search.toLowerCase();
    return normalizedOptions.filter(opt =>
      (opt.label || '').toLowerCase().includes(term) ||
      (opt.value || '').toLowerCase().includes(term)
    );
  }, [normalizedOptions, search]);

  const selectedOption = normalizedOptions.find(opt => String(opt.value) === String(value));
  const displayLabel = selectedOption ? selectedOption.label : '';

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', ...style }}>
      {/* Trigger Button */}
      <div
        id={id}
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          background: '#fff',
          border: isOpen ? '1.5px solid #3b82f6' : '1.5px solid #cbd5e1',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '14px',
          fontWeight: '500',
          color: displayLabel ? '#1e293b' : '#94a3b8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          minHeight: '38px',
          boxSizing: 'border-box',
          boxShadow: isOpen ? '0 0 0 3px rgba(59, 130, 246, 0.15)' : 'none',
          transition: 'all 0.2s ease',
          userSelect: 'none'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>
          {displayLabel || placeholder}
        </span>
        <FaChevronDown style={{ fontSize: '11px', color: '#64748b', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            padding: '8px',
            maxHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            ...menuStyle
          }}
        >
          {/* Search Input Box */}
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '12px' }} />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '8px 10px 8px 30px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
                background: '#f8fafc'
              }}
            />
            {search && (
              <FaTimes
                onClick={(e) => { e.stopPropagation(); setSearch(''); }}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '11px', cursor: 'pointer' }}
              />
            )}
          </div>

          {/* Options List */}
          <div style={{ overflowY: 'auto', flex: 1, maxHeight: '210px' }}>
            {/* Clear / Default Option */}
            <div
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              style={{
                padding: '8px 10px',
                fontSize: '13px',
                color: '#64748b',
                cursor: 'pointer',
                borderRadius: '6px',
                fontWeight: '500',
                fontStyle: 'italic',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              -- {placeholder} --
            </div>

            {filteredOptions.length === 0 ? (
              <div style={{ padding: '12px 10px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                No matching results found
              </div>
            ) : (
              filteredOptions.map((opt, i) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <div
                    key={opt.value || i}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      color: isSelected ? '#1d4ed8' : '#334155',
                      fontWeight: isSelected ? '700' : '500',
                      background: isSelected ? '#eff6ff' : 'transparent',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span>{opt.label}</span>
                    {isSelected && <FaCheckCircle style={{ color: '#3b82f6', fontSize: '12px' }} />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AddIngredientAndNutrition = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const bottomRef = useRef(null);
  const { baseUrl, getHeaders, configLoading } = useContext(UserContext);

  const [isMessageBoxVisible, setIsMessageBoxVisible] = useState(false);
  const { formData: passedFormData, itemId, isEditing = false, itemToEdit, type: passedType = "item", index: passedIndex = null, name: passedName } = location.state || {};

  const isSubForm = !!passedFormData;

  const getInitialIngredients = () => {
    if (!passedFormData) return [{ name: '', small: '', medium: '', large: '', weight: '', nutrition: [] }];

    let rawIngredients = [];
    if (passedType === "addons" || passedType === "addon") {
      rawIngredients = passedFormData.addons?.[passedIndex]?.ingredients || [];
    } else if (passedType === "combos" || passedType === "combo") {
      rawIngredients = passedFormData.combos?.[passedIndex]?.ingredients || [];
    } else {
      rawIngredients = passedFormData.ingredients || [];
    }

    if (rawIngredients.length === 0) {
      return [{ name: '', inventory_item_id: '', uom: '', quantity: '', small: '', medium: '', large: '', weight: '', nutrition: [] }];
    }

    return rawIngredients.map(ing => ({
      ...ing,
      name: ing.name || ing.ingredients_name || '',
      inventory_item_id: ing.inventory_item_id || '',
      uom: ing.uom || '',
      quantity: ing.quantity || '',
      small: ing.small || '',
      medium: ing.medium || '',
      large: ing.large || '',
      weight: ing.weight || ''
    }));
  };

  const getInitialItemName = () => {
    if (passedName) return passedName;
    if (passedFormData) return passedFormData.item_name || '';
    return '';
  };

  const [ingredients, setIngredients] = useState(
    isSubForm ? getInitialIngredients() : [{ name: '', inventory_item_id: '', uom: '', quantity: '', small: '', medium: '', large: '', weight: '', nutrition: [] }]
  );
  const [items, setItems] = useState([]);
  const [combos, setCombos] = useState([]);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(isSubForm ? getInitialItemName() : '');
  const [searchTerm, setSearchTerm] = useState(isSubForm ? getInitialItemName() : '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');

  // Permission State
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (configLoading) return;
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          const role = user.role || user.UserType;
          if (role) {
            const url = baseUrl ? `${baseUrl}/api/role-permissions?role=${encodeURIComponent(role)}` : `/api/role-permissions?role=${encodeURIComponent(role)}`;
            const response = await axios.get(url, { headers: getHeaders() });
            const perms = response.data.permissions || [];

            // Check for admin bypass
            const isAdmin = role.toLowerCase().includes('admin') || user.email?.includes('@temp.com');

            const pagePerm = perms.find(p => p.pageId === 'add_ingredients');
            if (isAdmin) {
              setCanWrite(true);
              setCanDelete(true);
            } else if (pagePerm) {
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };
    fetchPermissions();
  }, [baseUrl, configLoading]);

  const [saveLoading, setSaveLoading] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const nutritionNames = [
    'Carbohydrates', 'Calories', 'Carbs', 'Proteins', 'Fats', 'Fiber', 'Vitamins', 'Minerals', 'Water', 'Sugars', 'Starch', 'Amino Acids', 'Fatty Acids', 'Cholesterol', 'Antioxidants', 'Electrolytes', 'Glucose', 'Fructose', 'Sucrose', 'Lactose', 'Cellulose', 'Pectin', 'Essential Amino Acids', 'Non-Essential Amino Acids', 'Saturated Fats', 'Unsaturated Fats', 'Trans Fats', 'Omega-3 Fatty Acids', 'Omega-6 Fatty Acids', 'Monounsaturated Fats', 'Polyunsaturated Fats', 'Medium-Chain Fatty Acids', 'Short-Chain Fatty Acids', 'Long-Chain Fatty Acids', 'Essential Fatty Acids',
    'ALA (Alpha-Linolenic Acid)', 'EPA (Eicosapentaenoic Acid)', 'DHA (Docosahexaenoic Acid)', 'Vitamin A', 'Vitamin B1', 'Vitamin B2', 'Vitamin B3', 'Vitamin B5', 'Vitamin B6', 'Vitamin B7', 'Vitamin B9', 'Vitamin B12', 'Vitamin C', 'Vitamin D', 'Vitamin E', 'Vitamin K', 'Calcium', 'Phosphorus', 'Magnesium', 'Sodium', 'Potassium', 'Chloride', 'Sulfur', 'Iron', 'Zinc', 'Copper', 'Manganese', 'Iodine', 'Selenium', 'Molybdenum', 'Chromium', 'Fluoride', 'Boron', 'Silicon', 'Nickel', 'Vanadium', 'Cobalt',
    'Phytochemicals', 'Flavonoids', 'Polyphenols', 'Carotenoids', 'Anthocyanins', 'Catechins', 'Resveratrol', 'Quercetin', 'Lycopene', 'Lutein', 'Zeaxanthin', 'Beta-Carotene', 'Curcumin', 'Glucosinolates', 'Isoflavones', 'Lignans', 'Tannins', 'Phytosterols', 'Glycogen', 'Beta-Glucans', 'Sterols', 'Probiotics', 'Prebiotics', 'Resistant Starch', 'Soluble Fiber', 'Insoluble Fiber',
    'Net Carbohydrates', 'Total Sugars', 'Added Sugars', 'Sugar Alcohols', 'Peptides', 'Dipeptides', 'Tripeptides', 'Branched-Chain Amino Acids', 'Aromatic Amino Acids', 'Creatine', 'Carnitine', 'Taurine', 'Inositol', 'Choline', 'Folate', 'Biotin', 'Niacin', 'Riboflavin', 'Thiamine', 'Coenzyme Q10',
    'Citric Acid', 'Malic Acid', 'Lactic Acid', 'Tartaric Acid', 'Oxalic Acid', 'Organic Acids', 'Purines', 'Nitrates', 'Nitrites', 'Caffeine', 'Theobromine', 'Theophylline', 'Creatinine', 'Moisture', 'Ash', 'Ash Content', 'Total Nitrogen', 'Alcohol'
  ];

  const generateOptions = () => {
    const nameMap = {};

    // Process main items and their nested addons/combos
    items.forEach((item) => {
      const itemName = item.item_name || item.name1;
      if (!itemName) return;

      if (!nameMap[itemName]) {
        nameMap[itemName] = {
          label: `${itemName} (Item)`,
          value: itemName,
          type: 'item',
          instances: [{ item_id: item._id }],
        };
      } else {
        if (!nameMap[itemName].instances.some(inst => inst.item_id === item._id)) {
          nameMap[itemName].instances.push({ item_id: item._id });
        }
      }

      // Addons nested in items
      item.addons?.forEach((addon, index) => {
        const addonName = addon.name1 || addon.item_name;
        if (!addonName) return;

        const key = `${addonName}_addon`;
        if (!nameMap[key]) {
          nameMap[key] = {
            label: `${addonName} (Addon)`,
            value: addonName,
            type: 'addon',
            instances: [{ item_id: item._id, index }],
          };
        } else {
          nameMap[key].instances.push({ item_id: item._id, index });
        }
      });

      // Combos nested in items
      item.combos?.forEach((combo, index) => {
        const comboName = combo.name1 || combo.item_name;
        if (!comboName) return;

        const key = `${comboName}_combo`;
        if (!nameMap[key]) {
          nameMap[key] = {
            label: `${comboName} (Combo)`,
            value: comboName,
            type: 'combo',
            instances: [{ item_id: item._id, index }],
          };
        } else {
          nameMap[key].instances.push({ item_id: item._id, index });
        }
      });
    });

    // Process standalone combo offers
    combos.forEach((combo) => {
      const comboName = combo.item_name || combo.description || "Combo Offer";
      if (!nameMap[comboName]) {
        nameMap[comboName] = {
          label: `${comboName} (Combo Offer)`,
          value: comboName,
          type: 'item', // Standalone combos are treated like items in nutrition context
          instances: [{ item_id: combo._id }],
        };
      } else {
        nameMap[comboName].instances.push({ item_id: combo._id });
      }
    });

    return Object.values(nameMap).sort((a, b) => a.label.localeCompare(b.label));
  };

  const options = generateOptions();
  const filteredOptions = searchTerm.trim()
    ? options.filter((option) =>
      option.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  const fetchItemsData = async () => {
    if (configLoading) return;
    setLoading(true);
    try {
      const headers = getHeaders();
      const itemsUrl = baseUrl ? `${baseUrl}/api/items` : '/api/items';
      const combosUrl = baseUrl ? `${baseUrl}/api/combo-offer` : '/api/combo-offer';
      const purchaseUrl = baseUrl ? `${baseUrl}/api/purchase_items` : '/api/purchase_items';

      const [itemsRes, combosRes, purchaseRes] = await Promise.all([
        axios.get(itemsUrl, { headers }),
        axios.get(combosUrl, { headers }).catch(() => ({ data: [] })),
        axios.get(purchaseUrl, { headers }).catch(() => ({ data: [] }))
      ]);

      setItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      setCombos(Array.isArray(combosRes.data) ? combosRes.data : []);
      setPurchaseItems(Array.isArray(purchaseRes.data) ? purchaseRes.data : []);
      setError(null);
    } catch (error) {
      console.error('Error fetching items:', error);
      setError('Failed to fetch items. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchItemNutrition = async (selectedValue) => {
    if (!selectedValue || isSubForm || configLoading) {
      setIngredients(getInitialIngredients());
      setHasExistingData(false);
      return;
    }

    const option = options.find((opt) => opt.value === selectedValue);
    if (!option) return;

    const { type, instances } = option;
    const firstInstance = instances[0];

    try {
      const url = baseUrl
        ? `${baseUrl}/api/items/nutrition/${encodeURIComponent(selectedValue)}`
        : `/api/items/nutrition/${encodeURIComponent(selectedValue)}`;

      const response = await axios.get(url, {
        params: {
          type,
          item_id: firstInstance.item_id,
          index: firstInstance.index
        },
        headers: getHeaders()
      });

      if (response.data) {
        const ingredientsData = response.data.ingredients || [];
        const validIngredients = ingredientsData.length > 0
          ? ingredientsData.map((ing, ingIndex) => ({
            name: ing.name || ing.ingredients_name || '',
            inventory_item_id: ing.inventory_item_id || '',
            uom: ing.uom || '',
            quantity: ing.quantity?.toString() || ing.weight?.toString() || '',
            small: ing.small?.toString() || '',
            medium: ing.medium?.toString() || '',
            large: ing.large?.toString() || '',
            weight: ing.weight?.toString() || '',
            nutrition: Array.isArray(ing.nutrition)
              ? ing.nutrition.map((nut, nutIndex) => ({
                nutrition_name: nut.nutrition_name || '',
                nutrition_value: nut.nutrition_value?.toString() || '',
                id: `${ingIndex}-${nutIndex}-${Date.now()}`,
              }))
              : [],
          }))
          : [{ name: '', inventory_item_id: '', uom: '', quantity: '', small: '', medium: '', large: '', weight: '', nutrition: [] }];

        setIngredients(validIngredients);
        setHasExistingData(ingredientsData.length > 0);
      }
    } catch (error) {
      console.error('Error fetching nutrition data:', error);
      setIngredients([{ name: '', inventory_item_id: '', uom: '', quantity: '', small: '', medium: '', large: '', weight: '', nutrition: [] }]);
      setHasExistingData(false);
    }
  };

  useEffect(() => {
    fetchItemsData();
  }, [baseUrl, configLoading]);

  useEffect(() => {
    if (isSubForm) {
      setSelectedItem(getInitialItemName());
      setIngredients(getInitialIngredients());
      setHasExistingData(false);
    } else if (selectedItem) {
      fetchItemNutrition(selectedItem);
    }
  }, [selectedItem, items, combos, isSubForm, passedFormData]);

  // Auto-migrate old ingredients without inventory_item_id
  useEffect(() => {
    if (purchaseItems.length > 0 && ingredients.length > 0) {
      let changed = false;
      const updatedIngredients = ingredients.map(ing => {
        if (!ing.inventory_item_id && ing.name) {
          const match = purchaseItems.find(p => {
            const pName = p.itemName || p.item_name || p.name;
            return pName && pName.toLowerCase() === ing.name.toLowerCase();
          });
          if (match) {
            changed = true;
            return {
              ...ing,
              inventory_item_id: match._id,
              name: match.itemName || match.item_name || match.name,
              uom: match.masterUnit || match.uom || 'Nos'
            };
          }
        }
        return ing;
      });
      if (changed) {
        setIngredients(updatedIngredients);
      }
    }
  }, [purchaseItems, ingredients]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setIngredients(newIngredients);
  };

  const handleNutritionChange = (ingredientIndex, nutritionIndex, field, value) => {
    const newIngredients = [...ingredients];
    const nutrition = [...newIngredients[ingredientIndex].nutrition];
    nutrition[nutritionIndex] = { ...nutrition[nutritionIndex], [field]: value };
    newIngredients[ingredientIndex].nutrition = nutrition;
    setIngredients(newIngredients);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };

  const handleAddRow = () => {
    setIngredients([...ingredients, { name: '', small: '', medium: '', large: '', weight: '', nutrition: [] }]);
    scrollToBottom();
  };

  const handleRemoveRow = (index) => {
    const newIngredients = [...ingredients];
    newIngredients.splice(index, 1);
    setIngredients(newIngredients);
  };

  const handleAddNutrition = (ingredientIndex) => {
    const newIngredients = [...ingredients];
    newIngredients[ingredientIndex].nutrition = [
      ...newIngredients[ingredientIndex].nutrition,
      { nutrition_name: '', nutrition_value: '', id: `${ingredientIndex}-${newIngredients[ingredientIndex].nutrition.length}-${Date.now()}` },
    ];
    setIngredients(newIngredients);
    scrollToBottom();
  };

  const handleDeleteNutrition = (ingredientIndex, nutritionIndex) => {
    const newIngredients = [...ingredients];
    newIngredients[ingredientIndex].nutrition = newIngredients[ingredientIndex].nutrition.filter(
      (_, index) => index !== nutritionIndex
    );
    setIngredients(newIngredients);
  };

  const handleItemSelect = (option) => {
    setSelectedItem(option.value);
    setSearchTerm(option.label);
    setIsDropdownOpen(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(true);
    if (!value) setSelectedItem('');
  };

  const handleGoBack = () => {
    if (isSubForm) {
      navigate('/create-item', { state: { formData: passedFormData, isEditing, item: itemToEdit } });
    } else {
      navigate(-1);
    }
  };

  const handleSave = async () => {
    if (!canWrite) {
      setSaveMessage('You do not have permission to save.');
      return;
    }
    if (!selectedItem && !isSubForm) {
      setSaveMessage('Please select an item, addon, or combo');
      return;
    }

    const validIngredients = ingredients.filter((ing) => ing.name.trim());

    // Validation
    for (const ing of validIngredients) {
      if (!ing.quantity || isNaN(parseFloat(ing.quantity)) || parseFloat(ing.quantity) <= 0) {
        setSaveMessage(`Valid quantity required for: ${ing.name}`);
        return;
      }

      for (const nut of ing.nutrition) {
        if (!nut.nutrition_name.trim()) {
          setSaveMessage(`Nutrition name required for: ${ing.name}`);
          return;
        }
        if (isNaN(parseFloat(nut.nutrition_value)) || parseFloat(nut.nutrition_value) < 0) {
          setSaveMessage(`Valid nutrition value required for: ${nut.nutrition_name}`);
          return;
        }
      }
    }

    const formattedIngredients = validIngredients.map((ing) => ({
      name: ing.name,
      ingredients_name: ing.name,
      inventory_item_id: ing.inventory_item_id || '',
      uom: ing.uom || '',
      quantity: parseFloat(ing.quantity) || 0,
      small: parseFloat(ing.small) || 0,
      medium: parseFloat(ing.medium) || 0,
      large: parseFloat(ing.large) || 0,
      weight: parseFloat(ing.weight) || 0,
      nutrition: ing.nutrition.map((nut) => ({
        nutrition_name: nut.nutrition_name,
        nutrition_value: parseFloat(nut.nutrition_value),
      })),
    }));

    if (isSubForm) {
      let updatedFormData = { ...passedFormData };
      if (passedType === "addons" || passedType === "addon") {
        const addons = [...(updatedFormData.addons || [])];
        if (passedIndex !== null) addons[passedIndex] = { ...addons[passedIndex], ingredients: formattedIngredients };
        updatedFormData.addons = addons;
      } else if (passedType === "combos" || passedType === "combo") {
        const combos = [...(updatedFormData.combos || [])];
        if (passedIndex !== null) combos[passedIndex] = { ...combos[passedIndex], ingredients: formattedIngredients };
        updatedFormData.combos = combos;
      } else {
        updatedFormData.ingredients = formattedIngredients;
      }

      // Background instant-save to database for existing items
      const targetItemId = location.state?.itemId || location.state?.itemToEdit?._id;
      if (isEditing && targetItemId && selectedItem) {
        const option = options.find((opt) => opt.value === selectedItem);
        if (option) {
          const payload = {
            item_name: selectedItem,
            type: option.type,
            instances: option.instances,
            ingredients: formattedIngredients,
          };
          try {
            const url = baseUrl ? `${baseUrl}/api/items/nutrition` : '/api/items/nutrition';
            axios.post(url, payload, { headers: getHeaders() }).catch(e => console.error("Background save failed", e));
          } catch (err) {
            console.error("Failed to execute background save", err);
          }
        }
      }

      navigate('/create-item', { state: { formData: updatedFormData, isEditing, item: itemToEdit } });
    } else {
      const option = options.find((opt) => opt.value === selectedItem);
      if (!option) return;

      const { type, instances } = option;
      const payload = {
        item_name: selectedItem,
        type,
        instances,
        ingredients: formattedIngredients,
      };

      try {
        setSaveLoading(true);
        const url = baseUrl ? `${baseUrl}/api/items/nutrition` : '/api/items/nutrition';
        const response = await axios.post(url, payload, { headers: getHeaders() });

        if (response.status === 200) {
          setSaveMessage('Data saved successfully!');
          setHasExistingData(true);
          setTimeout(() => {
            if (location.state) {
              navigate('/create-item', { state: { formData: updatedFormData, isEditing, item: itemToEdit } });
            } else {
              navigate('/admin');
            }
          }, 1500);
        }
      } catch (error) {
        console.error('Save error:', error);
        setSaveMessage(error.response?.data?.error || 'Failed to save data');
      } finally {
        setSaveLoading(false);
      }
    }
  };

  const handleClear = async () => {
    if (!canDelete) {
      setSaveMessage('Permission denied.');
      return;
    }
    if (!selectedItem) return;

    const option = options.find((opt) => opt.value === selectedItem);
    if (!option) return;

    try {
      setSaveLoading(true);
      const url = baseUrl
        ? `${baseUrl}/api/items/nutrition/${encodeURIComponent(selectedItem)}`
        : `/api/items/nutrition/${encodeURIComponent(selectedItem)}`;

      const response = await axios.delete(url, {
        params: { type: option.type },
        headers: { ...getHeaders(), 'X-Instances': JSON.stringify(option.instances) }
      });

      if (response.status === 200) {
        setSaveMessage('Data cleared successfully!');
        setIngredients([{ name: '', small: '', medium: '', large: '', weight: '', nutrition: [] }]);
        setHasExistingData(false);
      }
    } catch (error) {
      setSaveMessage('Failed to clear data');
    } finally {
      setSaveLoading(false);
    }
  };

  const calculateNutrition = (ingredient, sizeKey) => {
    const baseWeight = parseFloat(ingredient.weight) || 1;
    const sizeWeight = parseFloat(ingredient[sizeKey]) || 0;
    const scalingFactor = sizeWeight / baseWeight;

    return ingredient.nutrition.map((nut) => {
      const val = parseFloat(nut.nutrition_value) || 0;
      return (val * scalingFactor).toFixed(3);
    });
  };

  const styles = {
    container: { height: '100vh', overflowY: 'auto', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)', padding: '20px', position: 'relative' },
    formContainer: { backgroundColor: '#ffffff', padding: '32px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.08)', width: '100%', maxWidth: '1000px', margin: '80px auto 20px' },
    title: { color: '#2c3e50', fontWeight: '700', marginBottom: '20px', paddingBottom: '15px', borderBottom: '3px solid #3498db', width: 'fit-content', textAlign: 'center', margin: '0 auto' },
    input: { width: '100%', padding: '12px', marginBottom: '15px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' },
    select: { width: '100%', padding: '12px', marginBottom: '15px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', backgroundColor: '#ffffff' },
    button: { padding: '10px 20px', borderRadius: '25px', border: '0', cursor: 'pointer', fontWeight: '600', transition: 'all 0.3s ease', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' },
    addButton: { backgroundColor: '#3498db', color: 'white' },
    deleteButton: { backgroundColor: '#e74c3c', color: 'white', padding: '8px 16px', marginTop: '10px' },
    saveButton: { backgroundColor: '#2ecc71', color: 'white' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' },
    tableHeader: { backgroundColor: '#f5f7fa', color: '#2c3e50', fontWeight: '600', padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' },
    tableCell: { padding: '10px', borderBottom: '1px solid #ddd', color: '#2c3e50', verticalAlign: 'top' },
    dropdown: { position: 'absolute', width: '100%', maxHeight: '250px', overflowY: 'auto', backgroundColor: '#ffffff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1002, marginTop: '4px' },
    dropdownItem: { padding: '12px 16px', cursor: 'pointer', transition: 'background-color 0.2s ease', borderBottom: '1px solid #f0f0f0' },
    messageBox: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#ffffff', padding: '25px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', zIndex: 2000, maxWidth: '400px', width: '90%', textAlign: 'center', border: '1px solid #3498db' },
    errorMessageBox: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: '#ffffff', padding: '25px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', zIndex: 2000, maxWidth: '400px', width: '90%', textAlign: 'center', border: '1px solid #dc2626' },
  };

  return (
    <div className="create-customer-container main-customer-page">
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={handleGoBack} className="header-back-btn">
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h2 className="header-title">Ingredients & Nutrition</h2>
        </div>
        <div className="header-right">
          <div className="header-actions">
            <button className="save-btn" onClick={handleSave} disabled={saveLoading || !canWrite}>
              <FaSave /> {saveLoading ? 'Saving...' : 'Save Data'}
            </button>
            <button
              onClick={handleClear}
              disabled={saveLoading || !hasExistingData || !canDelete}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0 20px',
                height: '44px',
                borderRadius: '12px',
                border: 'none',
                background: (saveLoading || !hasExistingData || !canDelete)
                  ? '#ccc'
                  : 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                color: '#fff',
                fontWeight: '700',
                fontSize: '14px',
                cursor: (saveLoading || !hasExistingData || !canDelete) ? 'not-allowed' : 'pointer',
                boxShadow: (saveLoading || !hasExistingData || !canDelete) ? 'none' : '0 4px 12px rgba(231,76,60,0.35)',
                transition: 'all 0.2s ease',
              }}
            >
              <FaTrash /> {saveLoading ? 'Processing...' : 'Clear Data'}
            </button>
          </div>
        </div>
      </div>
      <div className="header-divider"></div>

      <div className="customer-scroll-body">
        {loading ? (
          <div style={{ textAlign: 'center', marginTop: '100px', color: '#3498db', fontSize: '20px', fontWeight: 'bold' }}>Loading catalog...</div>
        ) : (
          <div className="form-sections-wrapper" style={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>

            {!isSubForm && (
              <div className="form-section-card theme-blue" ref={dropdownRef} style={{ marginBottom: '20px' }}>
                <div className="section-header">
                  <div className="section-icon"><FaUtensils /></div>
                  <h3 className="section-title">Select Item, Addon, or Combo</h3>
                </div>
                <div className="form-grid" style={{ padding: '20px', position: 'relative' }}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Search Catalog</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => setIsDropdownOpen(true)}
                      placeholder="Search catalog (e.g. 'watermelon', 'combo'...)"
                      className="form-control"
                    />
                    {isDropdownOpen && filteredOptions.length > 0 && (
                      <div style={styles.dropdown}>
                        {filteredOptions.map((opt, idx) => (
                          <div
                            key={idx}
                            style={styles.dropdownItem}
                            onClick={() => handleItemSelect(opt)}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#f8f9fa')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = '#ffffff')}
                          >
                            {opt.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isSubForm && (
              <div className="form-section-card theme-blue" style={{ marginBottom: '20px' }}>
                <div className="form-grid" style={{ padding: '20px', textAlign: 'center' }}>
                  <h4 style={{ color: '#3498db', margin: 0, width: '100%', gridColumn: '1 / -1' }}>
                    {passedType === 'item' ? 'Item' : passedType === 'addons' || passedType === 'addon' ? 'Addon' : 'Combo'}: {getInitialItemName() || 'Unnamed'}
                  </h4>
                </div>
              </div>
            )}

            {error && (
              <div style={{ color: '#e74c3c', marginBottom: '20px', textAlign: 'center', fontWeight: 'bold' }}>{error}</div>
            )}

            {(selectedItem || isSubForm) && (
              <>
                <div className="form-section-card theme-green" style={{ marginBottom: '20px' }}>
                  <div className="section-header">
                    <div className="section-icon"><FaLeaf /></div>
                    <h3 className="section-title">Ingredients</h3>
                  </div>
                  <div className="premium-card">
                    <table className="premium-table">
                      <thead>
                        <tr>
                          <th>Ingredient Name</th>
                          <th>Quantity</th>
                          <th>UOM</th>
                          <th style={{ textAlign: 'right' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ingredients.map((ing, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="form-group" style={{ margin: 0, position: 'relative' }}>
                                <SearchableDropdown
                                  options={purchaseItems.map(p => ({
                                    value: p._id,
                                    label: p.itemName || p.item_name || p.name
                                  }))}
                                  value={ing.inventory_item_id || ''}
                                  onChange={(selectedId) => {
                                    const selectedItem = purchaseItems.find(p => p._id === selectedId);
                                    setIngredients(prev => {
                                      const newIngs = [...prev];
                                      if (selectedItem) {
                                        newIngs[idx] = {
                                          ...newIngs[idx],
                                          inventory_item_id: selectedId,
                                          name: selectedItem.itemName || selectedItem.item_name || selectedItem.name,
                                          uom: selectedItem.masterUnit || selectedItem.uom || 'Nos'
                                        };
                                      } else {
                                        newIngs[idx] = {
                                          ...newIngs[idx],
                                          inventory_item_id: '',
                                          name: '',
                                          uom: ''
                                        };
                                      }
                                      return newIngs;
                                    });
                                  }}
                                  placeholder="Select Ingredient"
                                  style={{ minWidth: '180px' }}
                                />
                                {ing.name && !ing.inventory_item_id && (
                                  <small style={{color: 'orange'}}>Migrate: {ing.name}</small>
                                )}
                              </div>
                            </td>
                            
                            <td>
                              <div className="form-group" style={{ margin: 0 }}>
                                <input
                                  className="form-control"
                                  type="number"
                                  value={ing.quantity}
                                  onFocus={(e) => (String(e.target.value) === '0') && handleIngredientChange(idx, 'quantity', '')}
                                  onBlur={(e) => e.target.value === '' && handleIngredientChange(idx, 'quantity', 0)}
                                  onChange={(e) => handleIngredientChange(idx, 'quantity', e.target.value)}
                                  style={{ background: '#fff', border: '1.5px solid #d1d5db', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', fontWeight: '500', color: '#2c3e50', outline: 'none', width: '100%', minWidth: '90px' }}
                                />
                              </div>
                            </td>
                            <td>
                              <div className="form-group" style={{ margin: 0 }}>
                                <select
                                  className="form-control"
                                  value={ing.uom || ''}
                                  onChange={(e) => handleIngredientChange(idx, 'uom', e.target.value)}
                                  style={{ background: '#fff', border: '1.5px solid #d1d5db', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', fontWeight: '500', color: '#2c3e50', outline: 'none', width: '100%', minWidth: '90px' }}
                                >
                                  <option value="">Select UOM</option>
                                  {(() => {
                                    const pItem = purchaseItems.find(p => p._id === ing.inventory_item_id);
                                    if (!pItem) return null;
                                    const units = [...new Set([pItem.nosUnit, pItem.packetUnit, pItem.boxUnit, pItem.uom, pItem.masterUnit, pItem.unit])].filter(Boolean);
                                    return units.map(u => <option key={u} value={u}>{u}</option>);
                                  })()}
                                </select>
                              </div>
                            </td>
                            <td className="actions-cell" style={{ textAlign: 'right' }}>
                              <button
                                className="action-btn action-btn-delete"
                                onClick={() => handleRemoveRow(idx)}
                                title="Remove Ingredient"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ padding: '15px 20px', display: 'flex', gap: '15px' }}>
                      <button
                        onClick={handleAddRow}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 22px',
                          borderRadius: '12px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #06D6A0 0%, #04a87d 100%)',
                          color: '#fff',
                          fontWeight: '700',
                          fontSize: '14px',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(6,214,160,0.35)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <FaPlus style={{ fontSize: '13px' }} /> Add Ingredient
                      </button>
                      <button
                        onClick={handleSave}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 22px',
                          borderRadius: '12px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          color: '#fff',
                          fontWeight: '700',
                          fontSize: '14px',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(59,130,246,0.35)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <FaSave style={{ fontSize: '13px' }} /> Save Ingredients
                      </button>
                    </div>
                  </div>
                </div>

                <div className="form-section-card theme-blue" style={{ marginBottom: '20px' }}>
                  <div className="section-header">
                    <div className="section-icon"><FaUtensils /></div>
                    <h3 className="section-title">Nutrition Values</h3>
                  </div>
                  <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
                    {ingredients.map((ing, ingIdx) => ing.name && (
                      <div key={ingIdx} style={{ padding: '20px', border: '1px solid #e2e8f0', borderRadius: '16px', background: '#ffffff', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '12px' }}>
                          <h6 style={{ fontWeight: '800', color: '#1e293b', margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }}></span>
                            {ing.name} Nutrition
                          </h6>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <div style={{ flex: '1 1 20%' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Small (GM)</label>
                              <input className="form-control" type="number" value={ing.small || ''} onChange={(e) => handleIngredientChange(ingIdx, 'small', e.target.value)} style={{ width: '100%', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: '8px', padding: '6px 10px', fontSize: '13px' }} />
                            </div>
                            <div style={{ flex: '1 1 20%' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Medium (GM)</label>
                              <input className="form-control" type="number" value={ing.medium || ''} onChange={(e) => handleIngredientChange(ingIdx, 'medium', e.target.value)} style={{ width: '100%', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: '8px', padding: '6px 10px', fontSize: '13px' }} />
                            </div>
                            <div style={{ flex: '1 1 20%' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Large (GM)</label>
                              <input className="form-control" type="number" value={ing.large || ''} onChange={(e) => handleIngredientChange(ingIdx, 'large', e.target.value)} style={{ width: '100%', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: '8px', padding: '6px 10px', fontSize: '13px' }} />
                            </div>
                            <div style={{ flex: '1 1 20%' }}>
                              <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Base Weight (GM)</label>
                              <input className="form-control" type="number" value={ing.weight || ''} onChange={(e) => handleIngredientChange(ingIdx, 'weight', e.target.value)} style={{ width: '100%', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: '8px', padding: '6px 10px', fontSize: '13px' }} />
                            </div>
                          </div>

                          {ing.nutrition.map((nut, nutIdx) => (
                            <div key={nutIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', padding: '10px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                              <SearchableDropdown
                                options={nutritionNames}
                                value={nut.nutrition_name}
                                onChange={(val) => handleNutritionChange(ingIdx, nutIdx, 'nutrition_name', val)}
                                placeholder="Select Nutrient"
                                style={{ flex: 1 }}
                              />

                              <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden', width: '120px' }}>
                                <input
                                  type="number"
                                  value={nut.nutrition_value}
                                  onFocus={(e) => (String(e.target.value) === '0') && handleNutritionChange(ingIdx, nutIdx, 'nutrition_value', '')}
                                  onBlur={(e) => e.target.value === '' && handleNutritionChange(ingIdx, nutIdx, 'nutrition_value', 0)}
                                  onChange={(e) => handleNutritionChange(ingIdx, nutIdx, 'nutrition_value', e.target.value)}
                                  style={{ width: '100%', border: 'none', padding: '8px', fontSize: '14px', fontWeight: '600', color: '#334155', outline: 'none', textAlign: 'right' }}
                                />
                                <span style={{ padding: '8px 12px 8px 4px', fontSize: '13px', color: '#64748b', fontWeight: '500', background: '#f8fafc', borderLeft: '1px solid #cbd5e1' }}>b/w</span>
                              </div>

                              <button
                                onClick={() => handleDeleteNutrition(ingIdx, nutIdx)}
                                style={{ width: '36px', height: '36px', borderRadius: '8px', border: 'none', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#fca5a5'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; }}
                                title="Remove Nutrient"
                              >
                                <FaTrash size={14} />
                              </button>
                            </div>
                          ))}

                          <button
                            onClick={() => handleAddNutrition(ingIdx)}
                            style={{
                              marginTop: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              padding: '10px',
                              borderRadius: '10px',
                              border: '1px dashed #cbd5e1',
                              background: 'transparent',
                              color: '#3b82f6',
                              fontWeight: '600',
                              fontSize: '14px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                          >
                            <FaPlus size={12} /> Add Nutrient
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-section-card theme-blue" style={{ marginBottom: '20px' }}>
                  <div className="section-header">
                    <div className="section-icon"><FaLeaf /></div>
                    <h3 className="section-title">Calculated Nutrition Summary</h3>
                  </div>
                  <div className="premium-card" style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '800px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '16px 20px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ingredient Name</th>
                            <th style={{ padding: '16px 20px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Small Portion</th>
                            <th style={{ padding: '16px 20px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Medium Portion</th>
                            <th style={{ padding: '16px 20px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Large Portion</th>
                            <th style={{ padding: '16px 20px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Base Portion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ingredients.filter(ing => ing.name).map((ing, idx) => {
                            const isEven = idx % 2 === 0;
                            return (
                              <tr key={idx} style={{ backgroundColor: isEven ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '20px', verticalAlign: 'top', borderRight: '1px solid #f1f5f9' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
                                    <strong style={{ color: '#1e293b', fontSize: '15px' }}>{ing.name}</strong>
                                  </div>
                                </td>

                                {/* Small Column */}
                                <td style={{ padding: '20px', verticalAlign: 'top' }}>
                                  {ing.nutrition && ing.nutrition.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                      {ing.nutrition.filter(nut => nut.nutrition_name).map((nut, nIdx) => {
                                        const val = parseFloat(nut.nutrition_value) || 0;
                                        const baseW = parseFloat(ing.weight) || 1;
                                        const smallW = parseFloat(ing.small) || 0;
                                        return (
                                          <div key={nIdx} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '20px', fontSize: '13px', color: '#475569', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #e2e8f0' }}>
                                            <span style={{ color: '#64748b' }}>{nut.nutrition_name}:</span>
                                            <strong style={{ color: '#0ea5e9' }}>{((val * smallW) / baseW).toFixed(1)} gm</strong>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : <span style={{ color: '#94a3b8', fontSize: '14px', fontStyle: 'italic' }}>-</span>}
                                </td>

                                {/* Medium Column */}
                                <td style={{ padding: '20px', verticalAlign: 'top' }}>
                                  {ing.nutrition && ing.nutrition.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                      {ing.nutrition.filter(nut => nut.nutrition_name).map((nut, nIdx) => {
                                        const val = parseFloat(nut.nutrition_value) || 0;
                                        const baseW = parseFloat(ing.weight) || 1;
                                        const medW = parseFloat(ing.medium) || 0;
                                        return (
                                          <div key={nIdx} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '20px', fontSize: '13px', color: '#475569', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #e2e8f0' }}>
                                            <span style={{ color: '#64748b' }}>{nut.nutrition_name}:</span>
                                            <strong style={{ color: '#8b5cf6' }}>{((val * medW) / baseW).toFixed(1)} gm</strong>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : <span style={{ color: '#94a3b8', fontSize: '14px', fontStyle: 'italic' }}>-</span>}
                                </td>

                                {/* Large Column */}
                                <td style={{ padding: '20px', verticalAlign: 'top' }}>
                                  {ing.nutrition && ing.nutrition.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                      {ing.nutrition.filter(nut => nut.nutrition_name).map((nut, nIdx) => {
                                        const val = parseFloat(nut.nutrition_value) || 0;
                                        const baseW = parseFloat(ing.weight) || 1;
                                        const largeW = parseFloat(ing.large) || 0;
                                        return (
                                          <div key={nIdx} style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '20px', fontSize: '13px', color: '#475569', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #e2e8f0' }}>
                                            <span style={{ color: '#64748b' }}>{nut.nutrition_name}:</span>
                                            <strong style={{ color: '#f59e0b' }}>{((val * largeW) / baseW).toFixed(1)} gm</strong>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : <span style={{ color: '#94a3b8', fontSize: '14px', fontStyle: 'italic' }}>-</span>}
                                </td>

                                {/* Base Column */}
                                <td style={{ padding: '20px', verticalAlign: 'top', borderLeft: '1px solid #f1f5f9', background: isEven ? '#fafafa' : '#f8fafc' }}>
                                  {ing.nutrition && ing.nutrition.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                      {ing.nutrition.filter(nut => nut.nutrition_name).map((nut, nIdx) => (
                                        <div key={nIdx} style={{ background: '#ffffff', padding: '4px 10px', borderRadius: '20px', fontSize: '13px', color: '#475569', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                          <span style={{ color: '#64748b' }}>{nut.nutrition_name}:</span>
                                          <strong style={{ color: '#10b981' }}>{parseFloat(nut.nutrition_value || 0).toFixed(1)} gm</strong>
                                        </div>
                                      ))}
                                    </div>
                                  ) : <span style={{ color: '#94a3b8', fontSize: '14px', fontStyle: 'italic' }}>-</span>}
                                </td>
                              </tr>
                            );
                          })}

                          {ingredients.filter(ing => ing.name).length === 0 && (
                            <tr>
                              <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '15px' }}>
                                No ingredients added yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div ref={bottomRef} style={{ height: '20px' }}></div>
              </>
            )}
          </div>
        )}
      </div>

      {saveMessage && (
        <div className="warning-modal-overlay">
          <div className="warning-modal" style={{ borderTop: saveMessage.includes('success') ? '4px solid #06D6A0' : '4px solid #f39c12' }}>
            <div className="warning-icon-wrapper" style={{ background: saveMessage.includes('success') ? '#e6fcf5' : '#fef4e6', color: saveMessage.includes('success') ? '#06D6A0' : '#f39c12' }}>
              {saveMessage.includes('success') ? <FaCheckCircle /> : <FaExclamationTriangle />}
            </div>
            <h3>{saveMessage.includes('success') ? 'Success' : 'Notice'}</h3>
            <p>{saveMessage}</p>
            <div className="warning-modal-actions">
              <button
                className="confirm-delete-btn"
                onClick={() => setSaveMessage('')}
                style={{ backgroundColor: saveMessage.includes('success') ? '#06D6A0' : '#f39c12', color: 'white', width: '100%', margin: '0' }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddIngredientAndNutrition;

